/**
 * O que faz: Busca e prepara os leads que receberam disparo de follow-up no período.
 * Onde usa: Diálogo de leads disparados dentro da aba Follow-up.
 * Conexões: TanStack Query, Supabase, follow_up_execucoes, leads e follow_up_campaigns.
 * Tabelas: SELECT em follow_up_execucoes com joins de leads e follow_up_campaigns.
 * Data: 27/08/2026
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface FollowupDispatchedLead {
  leadId: string;
  clinicaId: string | null;
  nome: string | null;
  telefone: string | null;
  adName: string | null;
  campaignId: string;
  campaignName: string;
  lastSentAt: string;
  respondedAt: string | null;
  receivedFollowups: number;
}

export interface FollowupDispatchedExecutionRow {
  id: string;
  lead_id: string;
  campaign_id: string;
  data_enviado: string | null;
  responded_at: string | null;
  lead: {
    id: string;
    clinica_id: string | null;
    nome: string | null;
    telefone: string | null;
    ad_name: string | null;
  } | null;
  campaign: {
    id: string;
    nome: string | null;
  } | null;
}

export interface UseFollowupDispatchedLeadsOptions {
  enabled: boolean;
  startDate: Date | null;
  endDate: Date | null;
  campaignIds: string[];
  campaignId?: string | null;
}

// Mantém uma linha por pessoa para espelhar a leitura do Dashboard, sem esconder quantos disparos ela recebeu.
export const groupDispatchedFollowupExecutions = (
  rows: FollowupDispatchedExecutionRow[],
): FollowupDispatchedLead[] => {
  const byLeadId = new Map<string, FollowupDispatchedLead>();

  for (const row of rows) {
    // Uma execução sem vínculo completo não abre chat com segurança; ela não entra na lista operacional.
    if (!row.data_enviado || !row.lead || !row.campaign) continue;

    const existing = byLeadId.get(row.lead.id);
    if (existing) {
      existing.receivedFollowups += 1;
      // Garante campanha e data do disparo mais recente mesmo quando a ordem das linhas varia.
      if (row.data_enviado > existing.lastSentAt) {
        existing.campaignId = row.campaign.id;
        existing.campaignName = row.campaign.nome?.trim() || 'Campanha sem nome';
        existing.lastSentAt = row.data_enviado;
        existing.respondedAt = row.responded_at;
      }
      continue;
    }

    byLeadId.set(row.lead.id, {
      leadId: row.lead.id,
      clinicaId: row.lead.clinica_id,
      nome: row.lead.nome,
      telefone: row.lead.telefone,
      adName: row.lead.ad_name,
      campaignId: row.campaign.id,
      campaignName: row.campaign.nome?.trim() || 'Campanha sem nome',
      lastSentAt: row.data_enviado,
      respondedAt: row.responded_at,
      receivedFollowups: 1,
    });
  }

  return Array.from(byLeadId.values());
};

export const useFollowupDispatchedLeads = ({
  enabled,
  startDate,
  endDate,
  campaignIds,
  campaignId = null,
}: UseFollowupDispatchedLeadsOptions) => {
  const startIso = startDate?.toISOString() ?? null;
  const endIso = endDate?.toISOString() ?? null;
  const authorizedCampaignIds = [...new Set(campaignIds)].sort();
  const effectiveCampaignIds = campaignId ? [campaignId] : authorizedCampaignIds;

  return useQuery({
    queryKey: ['followup-dispatched-leads', startIso, endIso, campaignId, effectiveCampaignIds],
    enabled: enabled && effectiveCampaignIds.length > 0,
    queryFn: async (): Promise<FollowupDispatchedLead[]> => {
      // Tabela: follow_up_execucoes | Operação: SELECT.
      // Campos: execução enviada, resposta, lead e campanha necessários para listar e abrir o chat.
      // Filtros: somente campanhas já autorizadas pela métrica visível, status enviado e data_enviado no período ativo.
      // Risco: mostra dados pessoais e histórico operacional; o RLS e a lista derivada da clínica impedem mistura de tenants.
      let query = (supabase as any)
        .from('follow_up_execucoes')
        .select(`
          id,
          lead_id,
          campaign_id,
          data_enviado,
          responded_at,
          lead:leads (
            id,
            clinica_id,
            nome,
            telefone,
            ad_name
          ),
          campaign:follow_up_campaigns (
            id,
            nome
          )
        `)
        .in('campaign_id', effectiveCampaignIds)
        .eq('status', 'enviado')
        .not('data_enviado', 'is', null)
        .order('data_enviado', { ascending: false });

      if (startIso) query = query.gte('data_enviado', startIso);
      if (endIso) query = query.lte('data_enviado', endIso);

      const { data, error } = await query;
      if (error) throw new Error(`Erro ao carregar leads disparados: ${error.message}`);

      return groupDispatchedFollowupExecutions(
        (data ?? []) as FollowupDispatchedExecutionRow[],
      );
    },
    staleTime: 30 * 1000,
  });
};
